import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, CheckCircle2, Loader2, RefreshCw, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../../utils/api';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';

const SEV_TONE = {
  critical: 'bg-red-50 text-red-600 border-red-100',
  high: 'bg-orange-50 text-orange-600 border-orange-100',
  medium: 'bg-amber-50 text-amber-600 border-amber-100',
  low: 'bg-slate-50 text-slate-500 border-slate-100',
};

/** Phase 4 Wave C — monitoring alerts + reconciliation status/run. */
export default function FinanceAlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [recon, setRecon] = useState(null);
  const [running, setRunning] = useState('');

  const load = useCallback(() => {
    apiCall('/api/admin/finance/alerts').then((d) => setAlerts(d.alerts || [])).catch(() => {});
    apiCall('/api/admin/finance/reconciliation').then((d) => setRecon(d.latest)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['finance', 'notifications'], load);

  const runRecon = async () => {
    setRunning('recon');
    try {
      const d = await apiCall('/api/admin/finance/reconciliation/run', { method: 'POST' });
      setRecon(d.run);
      toast[d.run.status === 'clean' ? 'success' : 'warning'](
        d.run.status === 'clean' ? 'Reconciliation clean — no discrepancies'
          : `Reconciliation found ${d.run.findings.length} issue type(s)`);
      load();
    } catch (e) {
      toast.error(e?.message || 'Reconciliation failed');
    } finally { setRunning(''); }
  };

  const runMonitoring = async () => {
    setRunning('mon');
    try {
      const d = await apiCall('/api/admin/finance/monitoring/run', { method: 'POST' });
      toast.success(d.result.new_alerts.length
        ? `Monitoring raised ${d.result.new_alerts.length} new alert(s)`
        : 'Monitoring pass — nothing new');
      load();
    } catch (e) {
      toast.error(e?.message || 'Monitoring run failed');
    } finally { setRunning(''); }
  };

  const resolve = async (id) => {
    try {
      await apiCall(`/api/admin/finance/alerts/${id}/resolve`, { method: 'POST' });
      setAlerts((prev) => prev.filter((a) => a._id !== id));
    } catch (e) {
      toast.error(e?.message || 'Failed to resolve');
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="finance-alerts-panel">
      <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <ShieldAlert className="w-4 h-4 text-red-500" /> Financial Monitoring Alerts
            <span className="text-[11px] font-normal text-gray-400">({alerts.length} open)</span>
          </h4>
          <button onClick={runMonitoring} disabled={!!running}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="run-monitoring-btn">
            {running === 'mon' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Scan now
          </button>
        </div>
        <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
          {alerts.length === 0 ? (
            <p className="px-4 py-6 text-xs text-gray-400 text-center flex items-center justify-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> No open alerts — all clear.
            </p>
          ) : alerts.map((a) => (
            <div key={a._id} className="px-4 py-2.5 flex items-center gap-3 text-xs" data-testid={`alert-${a.alert_type}`}>
              <span className={`px-2 py-0.5 rounded-full font-semibold border ${SEV_TONE[a.severity] || SEV_TONE.low}`}>{a.severity}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate">{a.title}</p>
                <p className="text-gray-400 truncate">{a.detail}</p>
              </div>
              <span className="text-gray-400 whitespace-nowrap">{a.created_at && new Date(a.created_at).toLocaleString()}</span>
              <button onClick={() => resolve(a._id)}
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
                data-testid={`resolve-alert-${a._id}`}>
                Resolve
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="reconciliation-card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Scale className="w-4 h-4 text-indigo-600" /> Reconciliation
          </h4>
          <button onClick={runRecon} disabled={!!running}
            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="run-reconciliation-btn">
            {running === 'recon' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Run now
          </button>
        </div>
        {!recon ? (
          <p className="text-xs text-gray-400">No reconciliation run yet — runs automatically every 6 hours.</p>
        ) : (
          <div className="space-y-2">
            <div className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${recon.status === 'clean' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`} data-testid="reconciliation-status">
              {recon.status === 'clean' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
              {recon.status === 'clean' ? 'All ledgers balanced' : `${recon.findings.length} discrepancy type(s)`}
            </div>
            <p className="text-[11px] text-gray-400">Last run {recon.run_at && new Date(recon.run_at).toLocaleString()} · {recon.checks_run} checks</p>
            {(recon.findings || []).map((f) => (
              <div key={f.check} className="text-xs bg-red-50/60 border border-red-100 rounded-lg px-2.5 py-1.5">
                <p className="font-semibold text-red-600">{f.check.replace(/_/g, ' ')} × {f.count}</p>
                <p className="text-gray-500">{f.detail}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
